import React, { useContext, useEffect, useState } from "react";
import { useClient } from "urql";
import { Dialog } from "@actinc/dls";
import { Loading } from "@actinc/dls/components";
import { ProgramsContext } from "../../../../programsProvider";
import { UsersContext } from "../../../../usersProvider";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { PreviewContent } from "../../../../components";
import { convertToUIStructure as itemConvertToUIStructure } from "../../../../stores/item";
import { convertToUIStructure as passageConvertToUIStructure } from "../../../../stores/passage";
import { getTaskPermissions } from "../../../../services/utils";

const PreviewContentDialog = ({ onClose, open, object }) => {
  const client = useClient();
  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);

  const [objectToPreview, setObjectToPreview] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!object?.id || !object?.type) return;

    if (object.type === "Item") {
      async function getItem() {
        setLoading(true);
        const response = await client
          .query(getItemQuery, { id: object.id })
          .toPromise();
        const data = {
          ...response.data?.itemByID,
          permissions: object.permissions,
        };
        if (data) {
          setObjectToPreview(
            await itemConvertToUIStructure(users)(programs)(data),
          );
        }
        setLoading(false);
      }
      getItem();
    } else if (object.type === "Passage") {
      async function getPassage() {
        setLoading(true);
        const response = await client
          .query(getPassageQuery, { id: object.id })
          .toPromise();
        const data = response.data?.passageByID;
        if (data) {
          setObjectToPreview(
            await passageConvertToUIStructure(users)(programs)(data),
          );
        }
        setLoading(false);
      }
      getPassage();
    }
  }, [client, object, programs, users]);

  const handleClose = (value) => () => {
    onClose(value);
  };

  return (
    <Dialog maxWidth="sm" onClose={handleClose(false)} open={open}>
      <DialogTitle>Preview Content</DialogTitle>
      <DialogContent>
        {loading ? (
          <Loading title="Loading..." />
        ) : (
          <PreviewContent objectToPreview={objectToPreview} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose(false)} variant="text">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const itemFields = `
  choiceInteractions {
    foils {
      content
      contentComments
      contentSuggestions
      correct
      distractorRationale
    }
    identifier
    maxChoices
    minChoices
  }
  content
  contentSuggestions
  extendedTextInteractions {
    expectedLength
    expectedLines
    identifier
    scoringCriteria
  }
  gapMatchInteractions {
    elements {
      content
      identifier
      matchGroup
      matchMax
    }
    identifier
    targets {
      correctResponse
      identifier
      matchGroup
      width
    }
  }
  graphicGapMatchInteractions {
    backgroundFileName
    backgroundHeight
    backgroundWidth
    correctResponses {
      element
      target
    }
    elements {
      identifier
      imageUrl
      matchMax
    }
    identifier
    targets {
      height
      identifier
      matchMax
      width
      xCoord
      yCoord
    }
  }
  hotspotInteractions {
    backgroundFileName
    backgroundHeight
    backgroundWidth
    elements {
      colorStyle
      correctResponse
      height
      identifier
      left
      radius
      shape
      top
      width
      xCoord
      yCoord
    }
    identifier
    maxChoices
  }
  id
  inlineChoiceInteractions {
    choices {
      correct
      text
    }
    distractorRationale
    identifier
  }
  matchInteractions {
    correctResponse {
      element
      target
    }
    elements {
      content
      identifier
      matchGroup
      matchMax
    }
    identifier
    maxElementsPerRow
    targets {
      identifier
      matchGroup
      matchMax
      text
      width
    }
  }
  name
  orderInteractions {
    correctResponse
    elements {
      content
      identifier
    }
    identifier
    orientation
  }
  textEntryInteractions {
    correctResponse
    expectedLength
    identifier
    ignoreCase
    patternMask
    placeholderText
  }
  versionID
`;

const getItemQuery = `
  query GetItem($id: ID!) {
    itemByID(id: $id) { ${itemFields} }
 }
`;

const passageFields = `
  content
  contentSuggestions
  id
  items {
    id
  }
  name
  versionID
`;

const getPassageQuery = `
  query GetPassage($id: ID!) {
    passageByID(id: $id) { ${passageFields} }
  }
`;

export { PreviewContentDialog };
